import clsx from 'clsx'
import { LinearGradient } from 'expo-linear-gradient'
import { useState, type ReactNode } from 'react'
import { View } from 'react-native'

import { Button } from '@/src/elements/button/default'
import { Card, CardBody, CardHeader } from '@/src/elements/card'
import { DateTimePicker } from '@/src/elements/input'
import { Typography } from '@/src/elements/text/Typography'
import { COMMUNITY_ACTIVITY, COMMUNITY_LIFETIME } from '@/src/features/community/models/community.constants'
import { TEST_ID } from '@/src/general/constants/testId'
import { NOVENA_IN_MS, ONE_DAY_IN_MS } from '@/src/general/constants/time'
import { ICON } from '@/src/general/enums/icons'
import { getDiffInDays, getStrictDiffInDays, isValidDate } from '@/src/general/primitives/dates/dayjs'
import { getDateWithoutTimezone } from '@/src/general/utils/getDateWithoutTimezone'
import { useTranslate } from '@/src/lang/useTranslate'

import { useJoinCommunityMutation } from '../../services/useJoinCommunity'
import { useCommunityJoinSteps } from '../hooks/useCommunityJoinSteps'
import { SubscribeJourneyLayout } from '../layouts/SubscribeJourneyLayout'

type TChooseDateScreenProps = {
  today: Date
  initialDate: Date
} & TIdSlugParam &
  TSearchParamsParam

export const ChooseDateScreen = ({ initialDate, today, idSlug, searchParams }: TChooseDateScreenProps): ReactNode => {
  const [isDateEditionDateEnabled, setIsDateEditionDateEnabled] = useState<boolean>(false)
  const [date, setDate] = useState<Date>(initialDate)
  const { t } = useTranslate()
  const isBeforeToday = getStrictDiffInDays(date, today) > 1

  const { community, goToNextStep, isPending, stepperOptions, shouldBeDisplayed } = useCommunityJoinSteps({
    stepName: 'date',
    idSlug,
    searchParams,
  })

  const { join } = useJoinCommunityMutation(community?.id ?? 0, searchParams, goToNextStep)

  if (!community || !shouldBeDisplayed) return null

  const cancelChooseAnotherDate = () => {
    setDate(initialDate)
    setIsDateEditionDateEnabled(false)
  }

  const toggleEditionDateMode = () => setIsDateEditionDateEnabled((v) => !v)

  let endDateComputed: string | undefined
  if (community.lifetime === COMMUNITY_LIFETIME.NOVENA) {
    endDateComputed = new Date(date.valueOf() + NOVENA_IN_MS).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })
  }
  if (community.lifetime === COMMUNITY_LIFETIME.RETREAT) {
    const retreatDurationInMs = getDiffInDays(community.fromDate, community.toDate) * ONE_DAY_IN_MS

    endDateComputed = new Date(date.valueOf() + retreatDurationInMs).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })
  }

  const isOnGoingCardVisible = community.activity === COMMUNITY_ACTIVITY.ONGOING && community.fromDate

  return (
    <SubscribeJourneyLayout
      icon={ICON.CALENDAR}
      stepperOptions={stepperOptions}
      title={t('community.join.options.title', { lifetime: community?.lifetime })}
      subtitle=""
      showBackButton
    >
      <View testID={TEST_ID.COMMUNITY.JOIN.CHOOSE_DATE}>
        {isOnGoingCardVisible && (
          <Card shadow="none" testID={TEST_ID.COMMUNITY.JOIN.GO_ONGOING}>
            <LinearGradient start={[0.28, 0]} locations={[0, 1]} colors={['#FFF0C7', '#FFE4C9']} className="p-2">
              <CardHeader>
                <Typography colors="primary" bold>
                  {t('community.join.infos', { nbMembers: community.nbMembers, lifetime: community?.lifetime })}
                </Typography>
              </CardHeader>
              <CardBody>
                <Button
                  fullWidth
                  variant="solid"
                  size="lg"
                  colors="primary"
                  testID={TEST_ID.COMMUNITY.JOIN.ONGOING_BUTTON}
                  isLoading={isPending}
                  onPress={() => join('ongoing')}
                >
                  {t('community.join.card.button', { lifetime: community.lifetime })}
                </Button>
                <Typography className="mt-4 self-center" colors="primary">
                  {t('community.join.from-date', {
                    diffDays:
                      getDiffInDays(
                        community.fromDate,
                        getDateWithoutTimezone(new Date(Date.now())) || new Date(Date.now()),
                      ) + 1,
                  })}
                </Typography>
              </CardBody>
            </LinearGradient>
          </Card>
        )}

        <Card testID={TEST_ID.COMMUNITY.JOIN.CHANGE_DATE} shadow="none" className="mt-4 bg-gradient-primary-medium p-3">
          <CardHeader>
            <Typography colors="primary" bold>
              {t('community.join.date', { lifetime: community?.lifetime })}
            </Typography>
          </CardHeader>

          {isDateEditionDateEnabled ? (
            <CardBody className="gap-4">
              <View className="flex-row items-center justify-between">
                <DateTimePicker
                  value={date || new Date()}
                  onChange={(newDate) => {
                    const dateWithoutTimezone = getDateWithoutTimezone(newDate)?.toDate()
                    if (dateWithoutTimezone) setDate(dateWithoutTimezone)
                  }}
                />
                <Button
                  isIconOnly
                  startIcon={ICON.CLOSE}
                  colors="primary"
                  contrast="low"
                  variant="light"
                  onPress={cancelChooseAnotherDate}
                  isLoading={isPending}
                />
              </View>
              {!isBeforeToday && isValidDate(date) && endDateComputed && (
                <>
                  {community.lifetime === COMMUNITY_LIFETIME.NOVENA && (
                    <Typography colors="primary" contrast="high">
                      {community.lifetime === COMMUNITY_LIFETIME.NOVENA &&
                        t('community.join.scheduled-end-date.novena', { date: endDateComputed })}
                    </Typography>
                  )}
                  {community.lifetime === COMMUNITY_LIFETIME.RETREAT && (
                    <Typography colors="primary" contrast="high">
                      {community.lifetime === COMMUNITY_LIFETIME.RETREAT &&
                        t('community.join.scheduled-end-date.retreat', { date: endDateComputed })}
                    </Typography>
                  )}
                </>
              )}
              {isBeforeToday && isValidDate(date) && (
                <Typography colors="primary" contrast="high">
                  {t('fromDate.in-the-futur')}
                </Typography>
              )}
              <Button
                variant="solid"
                size="lg"
                colors="primary"
                fullWidth
                onPress={() => join(date)}
                testID={TEST_ID.COMMUNITY.JOIN.GO_SELECTED_DATE}
                isDisabled={isBeforeToday}
                isLoading={isPending}
              >
                {t('community.join.date.validate')}
              </Button>
            </CardBody>
          ) : (
            <CardBody className={clsx({ 'flex-row': isOnGoingCardVisible, 'gap-4': isOnGoingCardVisible })}>
              <Button
                variant={isOnGoingCardVisible ? 'flat' : 'solid'}
                size="lg"
                className={clsx('my-2 web:flex-1', { 'native:flex-1': isOnGoingCardVisible })}
                colors="primary"
                fullWidth={!isOnGoingCardVisible}
                onPress={() => join('today')}
                testID={TEST_ID.COMMUNITY.JOIN.GO_TODAY}
                isLoading={isPending}
              >
                {t('word.today')}
              </Button>
              <Button
                variant="flat"
                size="lg"
                colors="primary"
                fullWidth={!isOnGoingCardVisible}
                onPress={toggleEditionDateMode}
                className={clsx('my-2 web:flex-1', { 'native:flex-1': isOnGoingCardVisible })}
                testID={TEST_ID.COMMUNITY.JOIN.ENABLE_DATE_EDITION}
                startIcon={ICON.CALENDAR}
                isLoading={isPending}
              >
                {t('word.choose')}
              </Button>
            </CardBody>
          )}
        </Card>
      </View>
    </SubscribeJourneyLayout>
  )
}
